/**
 * 47. RFQs — RFQ header; supplier lines; quote comparison; award.
 *
 * Arabic and English (C7): every word from the catalogs; statuses agree with
 * the request for quotation.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatDate, formatQuantity } from "@/lib/money";

interface RfqRow {
  id: string;
  rfqNumber: string;
  title: string;
  supplierCount: number;
  quoteCount: number;
  issueDate: string;
  responseDeadline: string;
  status: "Draft" | "Sent" | "QuotesReceived" | "Awarded" | "Cancelled";
}

export function RfqsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<RfqRow>(
    ["procurement", "rfqs"],
    "/procurement/rfqs",
  );

  return (
    <div>
      <PageHeader
        title={t("procurement.rfqs.title")}
        subtitle={t("procurement.rfqs.subtitle")}
        actions={
          <PendingButton variant="primary" reason="notScheduled" slice="B1">
            {t("procurement.rfqs.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("procurement.rfqs.list"), data.total)}>
          <DataTable<RfqRow>
            rows={data.items}
            columns={[
              {
                key: "rfqNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.rfqNumber}</span>
                ),
              },
              { key: "title", label: t("fields.title") },
              {
                key: "supplierCount",
                label: t("procurement.rfqs.suppliers"),
                numeric: true,
                render: (r) => formatQuantity(r.supplierCount, 0),
              },
              {
                key: "quoteCount",
                label: t("procurement.rfqs.quotes"),
                numeric: true,
                render: (r) => formatQuantity(r.quoteCount, 0),
              },
              {
                key: "issueDate",
                label: t("procurement.rfqs.issued"),
                render: (r) => formatDate(r.issueDate),
              },
              {
                key: "responseDeadline",
                label: t("procurement.rfqs.deadline"),
                render: (r) => formatDate(r.responseDeadline),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) =>
                  r.status === "Awarded" ? (
                    <StatusBadge status={r.status} type="Rfq" />
                  ) : (
                    <Badge
                      tone={
                        r.status === "Cancelled"
                          ? "danger"
                          : r.status === "QuotesReceived"
                            ? "primary"
                            : r.status === "Sent"
                              ? "warning"
                              : "default"
                      }
                    >
                      {statusLabel(t, "Rfq", r.status)}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
